import { dynamicTranslations } from "@shared/utils/dynamicTranslation";
import { View } from "react-native";
import Icon from "@shared/components/md-design-system/Icon/Icon";
import Button from "@shared/components/md-design-system/Button/Button";
import PressableScale from "@shared/components/md-design-system/Button/PressableScaleButton";
import { Text } from "@shared/components/text";
import { Head } from "@shared/components/head";
import { getFormattedTimestamp } from "@shared/utils/date";
import { ActivityType, IPathwayActivity } from "app/_modules/pathways/entities";
import { ActivityName } from "../../types";
import { colorPalette } from "@shared/config/colorsConfig";
import { useAuth } from "app/auth/hooks/useAuth";
import { useNavigation } from "@react-navigation/native";
import { extractMeasurementValues, formatMeasurementValue, IMeasurementData } from "app/_modules/core/utils";
import { generateActivityTitleTranslation } from "@shared/utils/dynamicTranslation";
import { usePost } from "app/queryProvider";
import { getQuestionnaireDisplayState } from "app/_modules/core/questionnaire";

export const ActivityCompleteCard = ({ activity, allowPress = true }: { activity: IPathwayActivity, allowPress?: boolean }) => {
    const { t } = dynamicTranslations();
    const { meData } = useAuth();
    const navigation = useNavigation<any>();
    const translatedTitle = generateActivityTitleTranslation(activity.activityName, activity.activityType);
    let displayValue: string | number = '';
    let displayUnit = '';
    let shouldShowValue = false;
    let showViewAnswersButton = false;

    const { mutate: generatePdf, isPending: isGeneratingPdf } = usePost<Record<string, never>, { pdfUrl: string }>({
        options: {
            onSuccess: ({ pdfUrl }) => {
                navigation.navigate('PdfDocumentPage', { pdfUrl });
            },
        },
    });

    const handleViewAnswers = () => {
        if (!activity.questionnaireId) return;
        generatePdf({
            resource: { name: 'questionnaire-results', id: activity.questionnaireResultId },
            url: '/generate-pdf',
            data: {},
        });
    };

    if (activity.activityName === ActivityName.STEP) {
        const stepValue = (activity.measurement)?.amount || 0;
        const vitalParameterData = meData?.current_pathway?.vital_parameters?.find(
            (vitalParameter) => vitalParameter.vital_parameter.name === ActivityName.STEP
        );
        const stepGoal = vitalParameterData?.amount

        const handleStepCardPress = () => {
            navigation.navigate('MeasurementFlow', {
                activityName: activity.activityName,
                activityId: activity.activityId
            });
        };

        return (
            <PressableScale
                onPress={handleStepCardPress}
                frameClassName="rounded-3xl border border-gray-300 bg-white"
                pressedFrameClassName="border-primary-700"
                contentClassName="p-4"
                pressedTintColor={colorPalette.primary[100]}
                disabled={!allowPress}
            >
                {({ pressed }) => (
                    <View className="flex-col gap-5">
                        <View className="flex-row items-center justify-between">
                            <Text className="font-semibold text-base">{t(translatedTitle)}</Text>
                            {allowPress && (
                                <View
                                    className={`min-w-9 min-h-9 rounded-full justify-center items-center border ${pressed
                                        ? 'border-primary-700 bg-primary-700'
                                        : 'border-gray-300 bg-white'
                                        }`}
                                    style={{ flexShrink: 0 }}>

                                    <Icon
                                        name="arrow-right"
                                        size={15}
                                        color={pressed ? "white" : "primary-700"}
                                    />

                                </View>
                            )}
                        </View>
                        <View className="flex-row items-center gap-2">
                            <Head type="h2">
                                {stepValue}{' '}
                                {stepGoal && (
                                    <Text className="text-gray-600 text-sm">
                                        {t('steps.steps-taken')} {stepGoal}
                                    </Text>
                                )}
                            </Head>
                        </View>
                        {(activity?.measurement)?.measuredAt && (
                            <Text className="text-sm">{getFormattedTimestamp((activity?.measurement)?.measuredAt?.toString() || activity.completedAt || '')}</Text>
                        )}
                    </View>
                )}
            </PressableScale>
        );
    }

    if (activity.activityType === ActivityType.PATHWAY_VITAL_PARAMETER && activity.measurement) {
        const measurement = activity.measurement as any;
        const measurementData: IMeasurementData = {
            type: activity.activityName,
            measurement: measurement,
            timestamp: String(measurement.measuredAt || activity.completedAt || '')
        };

        const extractedValues = extractMeasurementValues(measurementData);
        const formatted = formatMeasurementValue(activity.activityName, extractedValues, t);
        displayValue = formatted.value;
        displayUnit = formatted.showUnit ? formatted.unit : '';
        shouldShowValue = formatted.value !== '--' && formatted.value !== '';
    }

    if (activity.activityType === ActivityType.PATHWAY_DRUG) {
        const administered = activity.drugAdministered === true;
        displayValue = t(administered ? 'vitalParameters.drugs.administered' : 'vitalParameters.drugs.notAdministered');
        displayUnit = '';
        shouldShowValue = true;
    }

    if (activity.activityType === ActivityType.PATHWAY_QUESTIONNAIRE && activity.questionnaireResult) {
        const state = getQuestionnaireDisplayState(activity.questionnaireResult);
        displayValue = state.displayValue;
        shouldShowValue = state.shouldShowValue;
        showViewAnswersButton = state.showViewAnswersButton;
    }

    const hasPendingAlert = !!activity.pendingAlert && !activity.pendingAlert.isRemeasurement && allowPress;

    const handlePendingAlertPress = () => {
        if (!activity.pendingAlert) return;
        navigation.navigate('WhoMeasured', {
            measurementId: activity.pendingAlert.measId,
            vitalParameterName: activity.pendingAlert.vitPName,
        });
    };

    const cardContent = (
        <View className="flex-col gap-2">
            <View className="flex-row items-center gap-3">
                {hasPendingAlert ? (
                    <View className="w-8 h-8 rounded-full justify-center items-center" style={{ backgroundColor: colorPalette.warning[100] }}>
                        <Icon name="exclamation-circle" size={16} color={colorPalette.warning[500]} />
                    </View>
                ) : (
                    <View className="w-8 h-8 rounded-full bg-success-100 justify-center items-center">
                        <Icon name="check" size={16} color={colorPalette.success[700]} />
                    </View>
                )}
                <Text className="font-semibold text-base">{t(translatedTitle)}</Text>
            </View>
            {shouldShowValue && (
                <View className="flex-row items-center gap-1">
                    <Head type="h3">
                        {displayValue} {displayUnit}
                    </Head>
                </View>
            )}
            <Text className="text-md">{getFormattedTimestamp((activity?.measurement)?.measuredAt?.toString() || activity.completedAt || '')}</Text>
            {showViewAnswersButton && (
                <View className="self-start">
                    <Button
                        icon="eye"
                        variant="primary"
                        onPress={handleViewAnswers}
                        disabled={isGeneratingPdf}
                    >
                        {isGeneratingPdf
                            ? t('telemonitoring.report.downloading')
                            : t('screens.activities.questionnaire.viewAnswers')
                        }
                    </Button>
                </View>
            )}
            {hasPendingAlert && (
                <View className="self-start">
                    <Button
                        variant="tiffany"
                        onPress={handlePendingAlertPress}
                    >
                        {t('screens.activities.verifyMeasurement')}
                    </Button>
                </View>
            )}
        </View>
    );

    // TECHDEBT
    // Issue: https://app.clickup.com/t/86c8zq9bu
    // Description: This component handles all activity types (step, vital parameter, drug, questionnaire) with mixed logic
    // Tech Debt - split into dedicated card components per activity type to improve maintainability as more types are added
    return (
        <View className="border border-gray-300 rounded-3xl p-4 flex-col gap-2">
            {cardContent}
        </View>
    )
}